import { type ClientOptions } from 'graphql-ws';

import { getSinkError, type SinkErrorKind } from '../sink';

/** Normalised connection-lifecycle event. graphql-ws types listener args as `unknown`. */
export type WsConnectionEvent =
	| {
			/** Did graphql-ws begin a connection attempt? */
			type: 'connecting';
			/** Which consecutive attempt began, with 1 as the initial attempt? */
			attempt: number;
	  }
	| {
			/** Did the browser WebSocket open, without proving graphql-ws acknowledgement? */
			type: 'opened';
			/** Which consecutive attempt opened the browser WebSocket? */
			attempt: number;
	  }
	| {
			/** Did graphql-ws receive connection acknowledgement, not an operation response? */
			type: 'connected';
			/** Which consecutive attempt established this connection? */
			attempt: number;
			/** How many acknowledged connections has this client established? */
			connectionCount: number;
	  }
	| {
			/** Did the shared WebSocket close, ending all operations on it? */
			type: 'closed';
			/** Which WebSocket close code was reported, when available? */
			closeCode: number | null;
			/** Which WebSocket close reason was reported, when available? */
			reason: string | null;
			/** Did the socket report a clean close handshake, when available? */
			wasClean: boolean | null;
			/** How long had the acknowledged connection remained open? */
			connectedForMs: number | null;
	  }
	| {
			/** Did the socket report an error without proving its terminal close cause? */
			type: 'error';
			/** What real Error message was available; browser socket errors carry none? */
			message: string | null;
			/** Which normalized sink-error category describes the payload? */
			kind: SinkErrorKind;
	  };

// Browser close is a CloseEvent; Client.terminate() emits a close-like object, which from
// graphql-ws 5.16 is an Error.
const readCloseInfo = (
	event: unknown,
): { closeCode: number | null; reason: string | null; wasClean: boolean | null } => {
	if (typeof event !== 'object' || event === null) {
		return { closeCode: null, reason: null, wasClean: null };
	}
	const code = 'code' in event ? event.code : undefined;
	const reason = 'reason' in event ? event.reason : undefined;
	const wasClean = 'wasClean' in event ? event.wasClean : undefined;
	return {
		closeCode: typeof code === 'number' ? code : null,
		reason: typeof reason === 'string' ? reason : null,
		wasClean: typeof wasClean === 'boolean' ? wasClean : null,
	};
};

/** Reporting, pong deadline, and connection flag share the same socket events. */
export const createSocketListeners = ({
	onConnectionEvent,
	pongWaitTimeout,
	terminate,
}: {
	onConnectionEvent?: (event: WsConnectionEvent) => void;
	/** 0 disables the deadline. */
	pongWaitTimeout: number;
	terminate: () => void;
}): { listeners: ClientOptions['on']; isConnected: () => boolean } => {
	let attempt = 0;
	let connectionCount = 0;
	let connectedAt: number | null = null;
	let pongDeadline: ReturnType<typeof setTimeout> | undefined;

	const report = (event: WsConnectionEvent): void => {
		if (!onConnectionEvent) {
			return;
		}
		try {
			onConnectionEvent(event);
		} catch {
			// Avoid breaking the transport if reporting throws.
		}
	};

	const clearPongDeadline = (): void => {
		clearTimeout(pongDeadline);
		pongDeadline = undefined;
	};

	return {
		isConnected: () => connectedAt !== null,
		listeners: {
			connecting: () => {
				attempt += 1;
				report({ type: 'connecting', attempt });
			},
			opened: () => {
				report({ type: 'opened', attempt });
			},
			connected: () => {
				connectionCount += 1;
				connectedAt = Date.now();
				report({ type: 'connected', attempt, connectionCount });
				attempt = 0;
			},
			closed: (event) => {
				// Drop the deadline so it cannot kill a reconnecting socket.
				clearPongDeadline();
				report({
					type: 'closed',
					...readCloseInfo(event),
					connectedForMs: connectedAt === null ? null : Date.now() - connectedAt,
				});
				connectedAt = null;
			},
			// Only our outbound ping arms the deadline; only an inbound pong clears it.
			ping: (received) => {
				if (received || pongWaitTimeout <= 0) {
					return;
				}
				clearPongDeadline();
				pongDeadline = setTimeout(terminate, pongWaitTimeout);
			},
			pong: (received) => {
				if (received) {
					clearPongDeadline();
				}
			},
			error: (error) => {
				report({
					type: 'error',
					message: error instanceof Error ? error.message : null,
					kind: getSinkError(error).kind,
				});
			},
		},
	};
};
